// Pure helpers for the "log your time every workday" enforcement. No React,
// no data dependency.
//
// A workday with NEITHER a time entry NOR an absence (Urlaub/Krank) is a
// "missing day": the user skipped logging. The next time they open the app we
// trap them in a non-dismissable modal until every such past workday is
// accounted for — either with a time entry or by marking it as Urlaub/Krank.
//
// This is computed entirely client-side from data the app already loads
// (time_entries + absences), so it needs no database migration.
import { pad, todayIso } from './format.js';

// Master switch — flip to false to disable the forced catch-up entirely
// (mirrors the MFA kill-switch pattern in featureFlags.js).
export const TIME_ENFORCEMENT_ENABLED = true;

// Enforcement never reaches back before this date, so members aren't trapped
// behind months of historical gaps on first rollout. Set to the coming Monday
// at launch — the first workday people are actually expected to account for.
export const ENFORCEMENT_START_ISO = '2026-06-29';

// Hard cap on how far back we ever look. Stops the backlog from ballooning if
// someone has been away from the app for a long stretch (the hard block means
// they'd otherwise have to clear every single day before getting in).
export const MAX_LOOKBACK_DAYS = 14;

function isoFromDate(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function parseIso(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function maxIso(a, b) { return a > b ? a : b; }

// Mon–Fri are workdays. We don't model public holidays here; the catch-up
// modal lets the user mark such a day as Urlaub/frei instead.
export function isWorkday(iso) {
  const day = parseIso(iso).getDay(); // 0=Sun .. 6=Sat
  return day >= 1 && day <= 5;
}

// Human label for a missing day, e.g. "Montag, 29. Juni 2026".
export function formatMissingDay(iso) {
  return parseIso(iso).toLocaleDateString('de-DE', {
    weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
  });
}

// The past workdays the given user still owes a time entry for — oldest first.
// A day is owed when it's a workday strictly before today, on/after the
// effective start, with no time entry by the user and not covered by an
// absence.
export function computeMissingTimeDays({
  userId, timeEntries, absences, profileCreatedAt, today = todayIso(),
}) {
  if (!TIME_ENFORCEMENT_ENABLED || !userId) return [];

  // Effective start = latest of: feature start, the lookback floor, and the
  // user's account-creation date (so brand-new members start clean).
  const lookbackFloor = (() => {
    const d = parseIso(today);
    d.setDate(d.getDate() - MAX_LOOKBACK_DAYS);
    return isoFromDate(d);
  })();
  let start = maxIso(ENFORCEMENT_START_ISO, lookbackFloor);
  if (profileCreatedAt) {
    start = maxIso(start, isoFromDate(new Date(profileCreatedAt)));
  }
  if (start >= today) return [];

  // Days that already have at least one time entry by this user.
  const haveEntry = new Set(
    timeEntries.filter(te => te.user_id === userId).map(te => te.entry_date)
  );

  // This user's absences as [start,end] ranges for cover checks.
  const ranges = absences
    .filter(a => a.user_id === userId)
    .map(a => [a.start_date, a.end_date]);
  const covered = (iso) => ranges.some(([s, e]) => iso >= s && iso <= e);

  const out = [];
  const cursor = parseIso(start);
  const end = parseIso(today);
  while (cursor < end) {
    const iso = isoFromDate(cursor);
    if (isWorkday(iso) && !haveEntry.has(iso) && !covered(iso)) out.push(iso);
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
}
